import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import { REFERENCE_DATA_AUTH_EVENT } from '../../contexts/ReferenceDataContext';
import { mergeStoredUserFromMeData } from '../../utils/sessionUser';
import { can, PERMISSIONS, getPeopleBasePath } from '../../utils/permissions';

/** Same order as Settings → User Roles (`UserRolesPage.jsx`). */
const MODULE_ORDER = [
  'Teams',
  'Matches',
  'Assets',
  'Asset Handover',
  'My Assets',
  'People',
  'Reports',
  'General',
];

const FEATURE_ALIAS_MAP = {
  view_team_event_feedback: 'view_team_feedback',
};

const FEATURE_LABELS = {
  add_assets: 'Add Assets',
  edit_assets: 'Edit Assets',
  checkout: 'Check-Out',
  checkin: 'Check-In',
  add_employee: 'Add Employee',
  edit_employee: 'Edit Employee',
  delete_employee: 'Delete Employee',
  manage_employee_permissions: 'Manage Employee Permissions',
  view_all_assets: 'View All Assets',
  view_assigned_assets: 'View Assigned Assets',
  view_my_asset_handover: 'My Asset Handover',
  delete_assets: 'Delete Assets',
  view_asset_reports: 'Asset Reports',
  view_performance_reports: 'Performance Reports',
  add_team: 'Add Team',
  edit_team: 'Edit Team',
  delete_team: 'Delete Team',
  show_all_teams: 'Show All Teams',
  view_team_feedback: 'View Feedback',
  view_team_event_feedback: 'View Feedback',
  add_match: 'Add Match',
  edit_match: 'Edit Match',
  give_match_tl_feedback: 'Give TL Feedback',
  view_match_tl_feedback: 'View TL Feedback',
  give_supervisor_feedback: 'Give Supervisor Feedback',
  view_supervisor_feedback: 'View Supervisor Feedback',
  show_all_matches: 'Show All Matches',
};

const getFeatureLabel = (feature) => {
  const key = String(feature || '').trim();
  const canonical = FEATURE_ALIAS_MAP[key] || key;
  return (
    FEATURE_LABELS[canonical] ||
    FEATURE_LABELS[key] ||
    canonical.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
};

const UserPermissionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const peopleBase = getPeopleBasePath(location.pathname);
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [moduleOpenState, setModuleOpenState] = useState({});
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  // Tracks the last-saved overrides so we can detect unsaved changes
  const savedOverridesRef = useRef({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.getUserPermissions(id);
      if (!response?.success) return;

      const data = response.data || {};
      setUser(data.user || null);
      setPermissions(Array.isArray(data.permissions) ? data.permissions : []);
      const loadedOverrides = data.overrides || {};
      setOverrides(loadedOverrides);
      savedOverridesRef.current = loadedOverrides;
    } catch (error) {
      toast.error(error.message || 'Failed to load user permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!can(PERMISSIONS.MANAGE_EMPLOYEE_PERMISSIONS)) {
      toast.error('You do not have permission to manage employee permissions');
      navigate(peopleBase);
      return;
    }
    fetchData();
  }, [id, peopleBase, navigate]);

  /** Group by module; merge aliased features into one row like User Roles. */
  const groupedPermissions = useMemo(() => {
    const mergedByCanonical = {};
    permissions.forEach((permission) => {
      const rawFeature = String(permission?.feature || '').trim();
      if (!rawFeature || rawFeature === 'submit_team_event_feedback') return;
      if (rawFeature === 'view_team' || rawFeature === 'view_matches') return;
      const canonicalFeature = FEATURE_ALIAS_MAP[rawFeature] || rawFeature;
      const moduleName = String(permission.module || 'General').trim() || 'General';
      if (moduleName.toLowerCase() === 'admin') return;
      const key = `${moduleName}::${canonicalFeature}`;
      if (!mergedByCanonical[key]) {
        mergedByCanonical[key] = {
          ...permission,
          feature: canonicalFeature,
          module: moduleName,
        };
      }
    });
    return Object.values(mergedByCanonical).reduce((acc, permission) => {
      const moduleName = String(permission.module || 'General').trim() || 'General';
      if (moduleName.toLowerCase() === 'admin') return acc;
      if (!acc[moduleName]) acc[moduleName] = [];
      acc[moduleName].push(permission);
      return acc;
    }, {});
  }, [permissions]);

  const sortedModuleNames = useMemo(
    () =>
      Object.keys(groupedPermissions).sort((a, b) => {
        const aIndex = MODULE_ORDER.indexOf(a);
        const bIndex = MODULE_ORDER.indexOf(b);
        const aOrder = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const bOrder = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.localeCompare(b);
      }),
    [groupedPermissions]
  );

  const sortedModulesKey = useMemo(() => sortedModuleNames.join('|'), [sortedModuleNames]);

  useEffect(() => {
    setModuleOpenState((prev) => {
      const next = {};
      sortedModuleNames.forEach((moduleName) => {
        next[moduleName] = prev[moduleName] ?? false;
      });
      return next;
    });
  }, [sortedModulesKey, sortedModuleNames]);

  const toggleModule = (moduleName) => {
    setModuleOpenState((prev) => ({
      ...prev,
      [moduleName]: !prev[moduleName],
    }));
  };

  // Whether every module section is currently expanded (drives the Expand/Collapse All button).
  const allModulesExpanded =
    sortedModuleNames.length > 0 && sortedModuleNames.every((moduleName) => moduleOpenState[moduleName]);

  const toggleAllModules = () => {
    const next = !allModulesExpanded;
    setModuleOpenState(() => {
      const state = {};
      sortedModuleNames.forEach((moduleName) => {
        state[moduleName] = next;
      });
      return state;
    });
  };

  const handleOverrideChange = (feature, value) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (value === 'default' || !value) {
        delete next[feature];
      } else {
        next[feature] = value;
      }
      return next;
    });
  };

  const getSelectionValue = (feature) => {
    const existing = overrides?.[feature];
    if (existing === 'allow' || existing === 'deny') return existing;
    return 'default';
  };

  // True when current overrides differ from the last-saved snapshot
  const isDirty = useMemo(() => {
    const saved = savedOverridesRef.current;
    const currentKeys = Object.keys(overrides);
    const savedKeys = Object.keys(saved);
    if (currentKeys.length !== savedKeys.length) return true;
    return currentKeys.some((k) => overrides[k] !== saved[k]);
  }, [overrides]);

  const handleBackClick = () => {
    if (isDirty) {
      setShowUnsavedModal(true);
    } else {
      navigate(peopleBase);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.updateUserPermissions(id, overrides);
      if (response?.success) {
        toast.success('User permissions updated');
        savedOverridesRef.current = { ...overrides };
        try {
          const current = JSON.parse(sessionStorage.getItem('user') || '{}');
          if (current?.token && String(current.id) === String(id)) {
            const me = await api.getCurrentUser();
            if (me?.success && me.data) {
              mergeStoredUserFromMeData(me.data);
              window.dispatchEvent(new Event(REFERENCE_DATA_AUTH_EVENT));
            }
          }
        } catch {
          /* ignore */
        }
        await fetchData();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div style={{ padding: '16px' }}>
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '16px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
            Individual Permissions
          </h2>
          <p style={{ margin: '8px 0 0 0', color: '#4a5568', fontSize: '13px' }}>
            {user?.name || 'User'} ({user?.role || 'N/A'}) - role default applies unless override is set.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleBackClick}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e0',
              background: 'white',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            Back
          </button>
          <button
            onClick={toggleAllModules}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e0',
              background: 'white',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '10px' }}>{allModulesExpanded ? '▲' : '▼'}</span>
            {allModulesExpanded ? 'Collapse All' : 'Expand All'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {saving ? 'Saving...' : 'Save Overrides'}
          </button>
        </div>
      </div>

      {sortedModuleNames.map((moduleName) => (
          <div
            key={moduleName}
            style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '12px',
              overflow: 'hidden',
            }}
          >
            <button
              type="button"
              onClick={() => toggleModule(moduleName)}
              style={{
                background: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                padding: '10px 12px',
                fontSize: '13px',
                fontWeight: 700,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                borderLeft: 'none',
                borderRight: 'none',
                borderTop: 'none',
              }}
            >
              <span>{moduleName}</span>
              <span style={{ fontSize: '14px', color: '#4a5568' }}>
                {moduleOpenState[moduleName] ? '▼' : '▶'}
              </span>
            </button>
            {moduleOpenState[moduleName] && <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '45%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '19%' }} />
                </colgroup>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', color: '#718096' }}>Feature</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '11px', color: '#718096' }}>Role Default</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '11px', color: '#718096' }}>Override</th>
                    <th style={{ textAlign: 'center', padding: '10px', fontSize: '11px', color: '#718096' }}>Effective</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedPermissions[moduleName]
                    .sort((a, b) => String(a.feature || '').localeCompare(String(b.feature || '')))
                    .map((permission) => {
                      const feature = permission.feature;
                      const selected = getSelectionValue(feature);
                      const roleAllowed = Boolean(permission.roleAllowed);
                      const effectiveAllowed = selected === 'allow'
                        ? true
                        : selected === 'deny'
                          ? false
                          : roleAllowed;

                      return (
                        <tr key={feature} style={{ borderBottom: '1px solid #edf2f7' }}>
                          <td style={{ padding: '10px', fontSize: '13px', color: '#2d3748', fontWeight: 600, wordBreak: 'break-word' }}>
                            {getFeatureLabel(feature)}
                          </td>
                          <td style={{ padding: '10px', textAlign: 'center', fontSize: '12px' }}>
                            {roleAllowed ? 'Allow' : 'Deny'}
                          </td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>
                            <select
                              value={selected}
                              onChange={(e) => handleOverrideChange(feature, e.target.value)}
                              style={{
                                padding: '6px 8px',
                                border: '1px solid #cbd5e0',
                                borderRadius: '6px',
                                fontSize: '12px',
                              }}
                            >
                              <option value="default">Default</option>
                              <option value="allow">Allow</option>
                              <option value="deny">Deny</option>
                            </select>
                          </td>
                          <td
                            style={{
                              padding: '10px',
                              textAlign: 'center',
                              fontSize: '12px',
                              fontWeight: 600,
                              fontStyle: selected !== 'default' ? 'italic' : 'normal',
                              color: effectiveAllowed ? '#2f855a' : '#c53030',
                            }}
                          >
                            {effectiveAllowed ? 'Allowed' : 'Denied'}
                            {selected !== 'default' && (
                              <span style={{ fontSize: '10px', marginLeft: '4px', opacity: 0.75 }}>
                                (override)
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>}
          </div>
        ))}

      {/* Unsaved changes warning modal */}
      {showUnsavedModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '10px',
              padding: '28px 24px 20px',
              width: '100%',
              maxWidth: '400px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <span style={{ fontSize: '22px' }}>⚠️</span>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#2d3748' }}>
                Unsaved Changes
              </h3>
            </div>
            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#4a5568', lineHeight: '1.6' }}>
              You have unsaved permission changes. If you go back now, your changes will be lost.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => setShowUnsavedModal(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e0',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#4a5568',
                }}
              >
                Stay
              </button>
              <button
                onClick={() => navigate(peopleBase)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#e53e3e',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                Leave without saving
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPermissionsPage;
